import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { SupplierRecords } from "./SupplierDetailsPage";
import DirectoryFilters, { defaultDirectoryFilters } from "../components/brand/DirectoryFilters";
import DataTable from "../components/brand/DataTable";
import Panel from "../components/brand/Panel";
import SearchIcon from "@mui/icons-material/Search";
import React, { useEffect, useState } from "react";
import { Alert, Box, Paper, Tabs, Tab, Link, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import Layout from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";
import { hasSupplierAccess } from "../utils/roleAccess";
import { getSuppliers, createSupplier, updateSupplier } from "../api/financeApi";
import { getApiErrorMessage } from "../utils/concurrency";

const fields = { name: "Name", contact_name: "Contact name", email: "Contact email", phone: "Phone", address: "Address" };
export default function SuppliersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "purchase-orders" ? "purchase-orders" : "suppliers";
  const { user, roleCapabilities } = useAuth();
  const allowed = hasSupplierAccess(user?.claims_role || user?.roles || [], roleCapabilities);
  const [data, setData] = useState({ results: [], total: 0 });
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [filters, setFilters] = useState(defaultDirectoryFilters);
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  useEffect(() => {
    if (!allowed || tab !== "suppliers") return;
    let current = true;
    setLoading(true);
    setError("");
    getSuppliers(page + 1, { page_size: size, search, ...filters }).then(({ data: result }) => {
      if (current) setData(result);
    }).catch((err) => { if (current) setError(getApiErrorMessage(err, "Unable to load suppliers.")); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [allowed, tab, page, size, search, refresh, filters]);
  const open = (supplier) => { setFormError(""); setForm(supplier || { name: "", contact_name: "", email: "", phone: "", address: "" }); };
  const save = async (event) => {
    event.preventDefault();
    setSaving(true); setFormError("");
    try {
      if (form.id) await updateSupplier(form.id, form);
      else await createSupplier(form);
      setToast("Supplier saved."); setForm(null); setRefresh((value) => value + 1);
    } catch (err) { setFormError(getApiErrorMessage(err, "Unable to save supplier. Close and reopen to load the latest version if it has changed.")); }
    finally { setSaving(false); }
  };
  return <Layout title="Suppliers" eyebrow="Shared across all properties" rightSlot={allowed && <Button variant="contained" startIcon={<AddIcon />} onClick={() => open()}>New supplier</Button>}>
    {!allowed ? <Alert severity="error">You do not have access to suppliers.</Alert> : <>
      <Paper elevation={2} sx={{ p: 0, borderRadius: 2 }}>
      <Box sx={{ px: 2, pt: 1 }}>
        <Tabs value={tab} variant="scrollable" scrollButtons="auto" onChange={(_, value) => {
          const next = new URLSearchParams(searchParams);
          if (value === "suppliers") next.delete("tab");
          else next.set("tab", value);
          setSearchParams(next);
        }}><Tab value="suppliers" label="Suppliers" /><Tab value="purchase-orders" label="Purchase Orders" /></Tabs>
      </Box>
      <Box sx={{ p: 2 }}><Box sx={{ mt: 2 }}>
      {tab === "purchase-orders" ? <SupplierRecords kind="purchase-orders" title="Purchase orders" hideHeader /> : <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
      <Box component="form" onSubmit={(event) => { event.preventDefault(); setSearch(searchDraft.trim()); setPage(0); }} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <TextField label="Search suppliers" placeholder="Search all fields" value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} size="small" sx={{ minWidth: 280 }} />
        <Button type="submit" variant="outlined" startIcon={<SearchIcon />}>Search</Button>
      </Box>
      <DirectoryFilters suppliers value={filters} onChange={(value) => { setFilters(value); setPage(0); }} />
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Panel>
      {loading ? <CircularProgress aria-label="Loading suppliers" /> : <Box sx={{ overflowX: "auto" }}><DataTable aria-label="Suppliers">
        <TableHead><TableRow>{["Name", "Contact", "Email", "Phone", "Address", "Status", "Actions"].map((label) => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
        <TableBody>{data.results.map((supplier) => <TableRow key={supplier.id} hover>
          {Object.keys(fields).map((field) => <TableCell key={field}>{field === "name" ? <Link component={RouterLink} to={`/suppliers/${supplier.id}`} underline="hover" sx={{ fontWeight: 600 }}>{supplier.name}</Link> : supplier[field] || "—"}</TableCell>)}
          <TableCell>{supplier.is_active ? "Active" : "Inactive"}</TableCell>
          <TableCell><Box sx={{ display: "flex", gap: 1 }}><Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={(event) => { event.stopPropagation(); open(supplier); }}>Edit</Button></Box></TableCell>
        </TableRow>)}{!data.results.length && <TableRow><TableCell colSpan={7}>No suppliers found.</TableCell></TableRow>}</TableBody>
      </DataTable></Box>}
      <TablePagination component="div" count={data.total} page={page} rowsPerPage={size} rowsPerPageOptions={[10, 25, 50]} onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(event) => { setSize(Number(event.target.value)); setPage(0); }} />
      </Panel>
      </>}
      </Box></Box>
      </Paper>
      <Dialog open={Boolean(form)} onClose={() => !saving && setForm(null)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={save}><DialogTitle>{form?.id ? "Edit supplier" : "New supplier"}</DialogTitle>
          <DialogContent sx={{ display: "grid", gap: 2, pt: "12px !important" }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            {Object.entries(fields).map(([field, label]) => <TextField key={field} label={label} required={["name", "email", "phone"].includes(field)} type={field === "email" ? "email" : field === "phone" ? "tel" : "text"} multiline={field === "address"} value={form?.[field] || ""} disabled={saving} onChange={(event) => setForm({ ...form, [field]: event.target.value })} />)}
          </DialogContent>
          <DialogActions><Button variant="outlined" startIcon={<CloseIcon />} disabled={saving} onClick={() => setForm(null)}>Close</Button><Button type="submit" variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}>Save supplier</Button></DialogActions>
        </Box>
      </Dialog>
      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast("")}><Alert severity="success" onClose={() => setToast("")}>{toast}</Alert></Snackbar>
    </>}
  </Layout>;
}
